import type { CityConfig } from "../city-template/types";

export const ashlandConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Ashland",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "ashland",
  route: "/cities/ashland",
  title: "Emergency Passport Ashland, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Ashland, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Boyd County and Tri-State travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Ashland, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Ashland and Tri-State area travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Ashland%20Kentucky%20downtown%20cityscape%20along%20the%20Ohio%20River%20at%20golden%20hour%2C%20historic%20downtown%20buildings%20with%20warm%20Appalachian%20sunset%20light%2C%20riverfront%20view%20with%20rolling%20green%20hills%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Northeastern%20Kentucky%20scene%2C%20Tri-State%20area%20overlooking%20Ohio%20and%20West%20Virginia%20hills&width=1800&height=700&seq=ashland-hero-001&orientation=landscape",
  heroImageAlt: "Ashland Kentucky downtown along the Ohio River at golden hour with historic buildings and Appalachian hills",
  geoPosition: "38.4784;-82.6379",
  geoPlacename: "Ashland, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 480 miles from Ashland via I-65 and I-90",
  },
  nearbyCitiesZips: [
    { name: "Ashland", zips: "41101, 41102, 41105" },
    { name: "Catlettsburg", zips: "41129" },
    { name: "Flatwoods", zips: "41139" },
    { name: "Russell", zips: "41169" },
    { name: "Ironton (OH)", zips: "45638" },
    { name: "Huntington (WV)", zips: "25701–25729" },
    { name: "Grayson", zips: "41143" },
    { name: "Louisa", zips: "41230" },
    { name: "Greenup", zips: "41144" },
    { name: "Morehead", zips: "40351" },
    { name: "Pikeville", zips: "41501, 41502" },
    { name: "Prestonsburg", zips: "41653" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Tri-State departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/louisville" },
    { name: "Fast passport renewal in Lexington", route: "/lexington" },
    { name: "Emergency passport help in Bowling Green", route: "/cities/bowling-green" },
    { name: "Urgent passport services in Huntington", route: "/cities/huntington" },
    { name: "Same-day passport help in Cincinnati", route: "/cincinnati" },
    { name: "Passport courier services near Pikeville", route: "/pikeville" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Ashland remotely via secure courier. We do not have a walk-in office in Ashland — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Boyd County travelers.",
  fpcOfficeAddress: "175 S Third St, Ste 200, Columbus, Ohio 43215",
  fpcOfficeDistance: "Serving all of Boyd County and the Tri-State area",
  serviceAreaIntro: "From downtown Ashland to the Catlettsburg area, from the riverfront to the rolling Appalachian hills — Fast Passport Center serves the entire Ashland community and surrounding Tri-State region. We regularly help travelers from Flatwoods, Russell, and Ironton get their passports fast.",
  nearbyCitiesIntro: "We serve all Ashland neighborhoods and nearby Tri-State area cities including Catlettsburg, Flatwoods, Russell, Ironton (OH), Huntington (WV), Grayson, Louisa, Greenup, Morehead, Pikeville, and Prestonsburg. If you are in Boyd County, eastern Kentucky, or the Ohio River Valley, we have you covered.",
  howItWorksIntro: "Most Ashland travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Ashland travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Ashland and Tri-State area travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Ashland? Here are the most common ones we hear from Boyd County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Boyd County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael S.",
      location: "Ashland, KY",
      rating: 5,
      text: "My daughter's passport expired the week before our family trip to the Bahamas. Fast Passport Center got her a new one in 48 hours. Saved our vacation.",
      service: "Child Passport",
    },
    {
      name: "Karen W.",
      location: "Flatwoods, KY",
      rating: 5,
      text: "Needed to renew my passport for a business trip to Toronto. I was nervous about the tight timeline, but they delivered in 4 days. Professional and reliable.",
      service: "Passport Renewal",
    },
    {
      name: "James T.",
      location: "Ironton, OH",
      rating: 5,
      text: "Lost my passport before a cruise out of Miami. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service from the Tri-State area.",
      service: "Lost Passport",
    },
    {
      name: "Rebecca H.",
      location: "Huntington, WV",
      rating: 5,
      text: "Living near the border, I wasn't sure which service to use. Fast Passport Center made it easy with overnight shipping. Had my emergency passport in 36 hours.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "ashland-1",
      name: "Ashland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1600 Greenup Ave",
      city: "Ashland",
      state: "KY",
      zip: "41101",
      phone: "(606) 329-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Ashland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Boyd County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-2",
      name: "Catlettsburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3400 Court St",
      city: "Catlettsburg",
      state: "KY",
      zip: "41129",
      phone: "(606) 739-5121",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Catlettsburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Boyd County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-3",
      name: "Flatwoods Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1920 Argillite Rd",
      city: "Flatwoods",
      state: "KY",
      zip: "41139",
      phone: "(606) 836-3121",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Flatwoods",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northern Boyd County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-4",
      name: "Huntington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 Virginia Ave W",
      city: "Huntington",
      state: "WV",
      zip: "25704",
      phone: "(304) 522-5521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Huntington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Tri-State area residents. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-5",
      name: "Ironton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "214 Park Ave",
      city: "Ironton",
      state: "OH",
      zip: "45638",
      phone: "(740) 532-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Ironton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Ohio River Valley residents across the river from Ashland.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-6",
      name: "Boyd County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2800 Louisa St",
      city: "Catlettsburg",
      state: "KY",
      zip: "41129",
      phone: "(606) 739-5116",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Boyd County Courthouse",
      website: "https://www.boydcountyky.gov/",
      websiteLabel: "Boyd County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 7 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Tri-State area photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Boyd County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ashland applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Boyd County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Tri-State area travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Ashland." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Ashland address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Ashland before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Ashland for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 7 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Ashland?", a: "Fast Passport Center offers fast passport renewal in Ashland in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Ashland in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Ashland?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 7 hour drive from Ashland — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Boyd County residents." },
    { q: "Can I expedite a child passport in Ashland?", a: "Yes. Fast Passport Center expedites child passports in Ashland for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Boyd County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Ashland with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Ashland?", a: "With Fast Passport Center's emergency courier service, Ashland travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Ashland?", a: "Popular options include the Ashland Post Office (41101), Catlettsburg Post Office (41129), Flatwoods Post Office (41139), Huntington Main Post Office (WV), and Boyd County Clerk (41129). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Ashland?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 7 hour drive to Chicago." },
    { q: "What if I live in a smaller Tri-State area town?", a: "We serve the entire Tri-State region including Catlettsburg, Flatwoods, Russell, Ironton (OH), Huntington (WV), Grayson, Louisa, Greenup, Morehead, Pikeville, and Prestonsburg. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Ashland?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Ashland?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Ashland and Huntington offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Ashland?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Ashland?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Boyd County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Ashland", address: "1550 Greenup Ave, Ashland, KY 41101", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Ashland", address: "4800 13th St, Ashland, KY 41102", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "Walmart — Ashland", address: "351 River Hill Dr, Ashland, KY 41101", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Ashland", address: "1310 29th St, Ashland, KY 41101", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "CVS Pharmacy — Huntington", address: "2000 3rd Ave, Huntington, WV 25703", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Huntington", address: "2500 US Route 60, Huntington, WV 25705", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Walgreens — Ironton", address: "2600 S 3rd St, Ironton, OH 45638", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Ashland", address: "711 29th St, Ashland, KY 41101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Boyd County",
      address: "2800 Louisa St, Catlettsburg, KY 41129",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Greenup County",
      address: "200 Main St, Greenup, KY 41144",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Carter County",
      address: "300 W Main St, Grayson, KY 41143",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for eastern Kentucky residents.",
      featured: true,
    },
    {
      name: "West Virginia DMV — Huntington",
      address: "5707 US Route 60, Huntington, WV 25705",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Alternative for West Virginia residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HTS",
      name: "Tri-State Airport (Huntington)",
      distance: "12 miles from downtown Ashland",
      address: "1449 Airport Rd, Huntington, WV 25704",
      note: "The primary airport for Boyd County and the Tri-State area. Domestic flights with connections to major international hubs.",
      flightUrl: "https://www.flytristate.com/",
      flightLabel: "Tri-State Airport",
      featured: true,
    },
    {
      code: "CRW",
      name: "Yeager Airport (Charleston)",
      distance: "60 miles from Ashland",
      address: "100 Airport Rd, Charleston, WV 25311",
      note: "Alternative regional airport with flights to major hubs.",
      flightUrl: "https://www.yeagerairport.com/",
      flightLabel: "Yeager Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "120 miles from Ashland",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-64.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Boyd County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.boydcountyky.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Boyd Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/Boyd.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};